<!-- Page Header -->
<app-title-bar
  title="Cash Reconciliation"
  description="Hi, {{ this.user?.name || '' }}"
  [showButton]="false"
>
</app-title-bar>

<!-- Stats Cards -->
<div class="stats-container">
  <app-stats-card
    *ngFor="let stat of orderStats; let i = index; trackBy: trackByIndex"
    [title]="stat.title"
    [value]="stat.value"
    [color]="stat.color"
    [icon]="stat.icon"
    [iconClass]="stat.iconClass"
    [iconStyle]="stat.iconStyle"
    [style.animation-delay]="i * 0.1 + 's'"
  >
  </app-stats-card>
</div>

<div class="scr-content">
  <div class="scr-header">
    <div
      class="scr-calendar-anchor"
      appClickOutside
      [appClickOutsideEnabled]="calendarOpen"
      (appClickOutside)="calendarOpen = false"
    >
      <button
        type="button"
        class="scr-calendar-btn"
        [class.scr-calendar-btn--active]="calendarOpen"
        (click)="toggleCalendar($event)"
        aria-label="Select date"
        [attr.aria-expanded]="calendarOpen"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="3"
            y="5"
            width="18"
            height="16"
            rx="3"
            stroke="currentColor"
            stroke-width="1.6"
          ></rect>
          <path d="M3 10.5H21" stroke="currentColor" stroke-width="1.6"></path>
          <path
            d="M8 3V7M16 3V7"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          ></path>
        </svg>
      </button>

      <div *ngIf="calendarOpen" class="scr-calendar-pop" (click)="$event.stopPropagation()">
        <app-wi-date-range-picker
          mode="date"
          [inline]="true"
          [showActions]="false"
          [selectedDate]="selectedDate"
          [disableDate]="disableFutureDates"
          (dateChange)="onReconciliationDateSelect($event)"
        >
        </app-wi-date-range-picker>
      </div>
    </div>
  </div>

  <section class="scr-card" aria-label="Attendance cash reconciliation">
    <div *ngIf="attendanceCashLoading" class="cr-loading">Loading...</div>
    <ng-container *ngIf="!attendanceCashLoading && attendanceCash as ac">
      <h2 class="scr-date">{{ formatAttendanceDetailTitle() }}</h2>
      <div class="scr-shift-pill-container">
        <span
          *ngIf="ac.shift && ac.shift !== '--'"
          class="attendance-shift-pill"
          [class.attendance-shift-pill--evening]="ac.shift === 'Evening'"
        >
          {{ getShiftLabel(ac.shift) }}
        </span>

        <div class="scr-fields">
          <div class="scr-field">
            <label class="scr-label">Opening Balance</label>
            <app-currency-action-input
              [value]="ac.openingBalance"
              [inputReadonly]="true"
              [showAction]="false"
              [allowDecimal]="true"
              [useNumberInput]="true"
              [minInput]="'1'"
            />
          </div>

          <div class="scr-field">
            <div class="scr-label-row">
              <label class="scr-label">Payment Received</label>
            </div>
            <div class="scr-field-content">
              <app-currency-action-input
                [value]="ac.paymentReceived"
                [inputReadonly]="true"
                [showAction]="false"
                [allowDecimal]="true"
                [useNumberInput]="true"
                [minInput]="'1'"
              />
              <button type="button" class="view-details-link" (click)="onViewPaymentDetails()">
                View Details
              </button>
            </div>
          </div>

          <div class="scr-field">
            <label class="scr-label">Expected Closing</label>
            <app-currency-action-input
              [value]="ac.expectedClosing"
              [inputReadonly]="true"
              [showAction]="false"
              [allowDecimal]="true"
              [useNumberInput]="true"
              [minInput]="'1'"
            />
          </div>

          <div class="scr-field">
            <label class="scr-label">Closing Balance</label>
            <app-currency-action-input
              [value]="closingSubmitted ? (ac.closingBalanceSubmitted ?? 0) : closingBalanceDraft"
              [allowNull]="true"
              [inputReadonly]="closingSubmitted"
              [showAction]="!closingSubmitted"
              [allowDecimal]="true"
              [useNumberInput]="true"
              [actionDisabled]="
                isSubmittedClosingDisabled || !canSubmitAttendanceClosing || submittingClosing
              "
              [buttonText]="submittingClosing ? '...' : 'Submit'"
              placeholder="Enter closing balance"
              (valueChange)="onClosingChange($event)"
              (action)="submitClosingBalance()"
              [minInput]="'1'"
            />
            <div
              class="scr-badge-wrap"
              *ngIf="
                closingSubmitted &&
                ac.matchStatus &&
                (ac.matchStatus === 'matched' || ac.matchStatus === 'unmatched')
              "
            >
              <app-cash-reconciliation-status-badge
                [status]="attendanceCash?.matchStatus || 'pending'"
                [delta]="attendanceCash?.delta ?? 0"
              />
            </div>
          </div>
        </div>

        <div class="scr-footer">
          <button
            type="button"
            class="cr-action-btn cr-action-btn--ghost"
            (click)="onGenerateReport()"
          >
            <span>View Report</span>
          </button>
        </div>
      </div>
    </ng-container>
  </section>
</div>

<!-- Payment Received -->
<app-wi-data-modal
  [isOpen]="paymentModalOpen"
  [title]="paymentModalOpen ? 'Cash Transactions — ' + paymentModal?.employee?.name : ''"
  width="700px"
  panelClass="cr-modal cr-modal--wide"
  [ariaLabel]="
    paymentModal ? 'Cash transactions for ' + paymentModal?.employee?.name : 'Cash transactions'
  "
  (modalClose)="closePaymentModal()"
>
  <p class="cr-modal__sub" modal-subtitle *ngIf="paymentModal as pm">
    {{ getFormattedDate() }} ·
    <span *ngIf="pm.shift" class="cr-modal__shift">
      {{ getShiftLabel(pm.shift) || '--' }}
    </span>
  </p>
  <ng-container *ngIf="paymentModal as pm">
    <div *ngIf="paymentTxLoading" class="cr-loading">Loading...</div>

    <div *ngIf="!paymentTxLoading && paymentTx.length === 0">
      <app-empty-result
        image="/assets/images/empty_screen.png"
        title="No records available"
        description="No cash reconciliation data available for this date."
        [showAction]="false"
      >
      </app-empty-result>
    </div>

    <div *ngIf="!paymentTxLoading && paymentTx.length !== 0" class="cr-tx-box mr-15">
      <div class="cr-tx-header">
        <span>Time</span>
        <span>Order ID</span>
        <span>Wash Type</span>
        <span class="r">Payment Received</span>
      </div>
      <div class="cr-tx-body">
        <div class="cr-tx-body-inner cr-overflow-auto">
          <div *ngFor="let t of paymentTx" class="cr-tx-row">
            <span>{{ t.time }}</span>
            <span>{{ t.orderId ? t.orderId : '--' }}</span>
            <span>{{ t.washType || '--' }}</span>
            <span class="r amt"
              >${{ formatCurrency(t.amount) }}
              <span *ngIf="t.kind === 'opening'" class="tx-pill">Opening balance</span>
              <span *ngIf="t.kind === 'order'" class="tx-pill">Order added cash</span>
              <span *ngIf="t.kind === 'extra'" class="tx-pill">Extra added cash</span>
              <span *ngIf="t.kind === 'closing'" class="tx-pill">Closing balance</span>
            </span>
          </div>
        </div>
        <!--<div class="cr-tx-total" *ngIf="!paymentTxLoading && paymentTx.length">
          <span>Total</span>
          <span>${{ formatCurrency(paymentTotal()) }}</span>
        </div>-->
      </div>
    </div>
  </ng-container>
</app-wi-data-modal>

<!-- Generate Reports — step 1 -->
<app-wi-data-modal
  *ngIf="reportTypeModalOpen"
  [isOpen]="reportTypeModalOpen"
  title="Generate Reports"
  width="480px"
  panelClass="cr-modal cr-modal--report cr-modal--report-type"
  ariaLabel="Generate Reports"
  (modalClose)="closeReportTypeModal()"
>
  <div class="cr-report-body">
    <p class="cr-report-choose">Choose report range</p>
    <div class="cr-report-radios">
      <label class="cr-report-radio">
        <input
          type="radio"
          name="reportRange"
          value="daily"
          [(ngModel)]="selectedReportType"
          (ngModelChange)="onReportRangeTypeChange()"
        />
        <span>Daily</span>
      </label>
      <label class="cr-report-radio">
        <input
          type="radio"
          name="reportRange"
          value="monthly"
          [(ngModel)]="selectedReportType"
          (ngModelChange)="onReportRangeTypeChange()"
        />
        <span>Monthly</span>
      </label>
      <label class="cr-report-radio">
        <input
          type="radio"
          name="reportRange"
          value="custom"
          [(ngModel)]="selectedReportType"
          (ngModelChange)="onReportRangeTypeChange()"
        />
        <span>Custom Range</span>
      </label>
    </div>
    <div class="cr-report-picker-slot">
      <!-- Daily -->
      <app-wi-date-range-picker
        *ngIf="selectedReportType === 'daily'"
        mode="date"
        [inline]="true"
        [showActions]="false"
        [selectedDate]="reportDate"
        [disableDate]="disableFutureDates"
        (dateChange)="onDateChange($event)"
      >
      </app-wi-date-range-picker>
      <!-- Monthly -->
      <app-wi-date-range-picker
        *ngIf="selectedReportType === 'monthly'"
        mode="monthly"
        [inline]="true"
        [showActions]="false"
        [disableDate]="disableFutureDates"
        [selectedMonth]="selectedMonth"
        [selectedYear]="selectedYear"
        (monthYearChange)="onMonthYearChange($event)"
      >
      </app-wi-date-range-picker>
      <!-- Custom Range -->
      <app-wi-date-range-picker
        *ngIf="selectedReportType === 'custom'"
        mode="fixed"
        [inline]="true"
        [showActions]="false"
        [disableDate]="disableFutureDates"
        [startDate]="fixStartDate"
        [endDate]="fixEndDate"
        (rangeChange)="onRangeChange($event)"
      >
      </app-wi-date-range-picker>
    </div>
    <div class="cr-report-actions">
      <button
        type="button"
        class="cr-report-btn cr-report-btn--secondary"
        (click)="cancelReportTypeModal()"
      >
        Cancel
      </button>
      <button
        type="button"
        class="cr-report-btn cr-report-btn--primary"
        [disabled]="!canSubmitReportGenerate"
        (click)="onReportGenerateClick()"
      >
        {{ reportLoading ? '…' : 'Generate' }}
      </button>
    </div>
  </div>
</app-wi-data-modal>

<!-- Generate Reports — step 2: result -->
<app-wi-data-modal
  *ngIf="reportResultModalOpen"
  [isOpen]="reportResultModalOpen"
  title="Cash Reconciliation Report"
  dialogId="cr-report-result-modal"
  width="900px"
  panelClass="cr-modal cr-modal--rep-past cr-modal--report cr-modal--report-result"
  ariaLabel="Cash Reconciliation Report"
  (modalClose)="closeReportResultModal()"
>
  <div class="cr-modal__head" modal-header>
    <h4 class="cr-modal__title">Cash Reconciliation Report</h4>
    <div class="cr-rep-head__right">
      <button
        type="button"
        class="cr-modal__x"
        (click)="closeReportResultModal()"
        aria-label="Close"
      >
        ×
      </button>
    </div>
  </div>
  <div class="cr-rep-body">
    <div class="data-modal__body--header">
      <p class="cr-rep-meta">
        <span class="cr-rep-dot">•</span>
        <span><strong>Date range:</strong> {{ reportDateRangeLabel }}</span>
        <span class="cr-rep-dot">•</span>
        <span><strong>Location:</strong> {{ reportLocationLabel }}</span>
        <span class="cr-rep-dot">•</span>
        <span><strong>Generated:</strong> {{ formatReportGeneratedAt(reportGeneratedAt) }}</span>
      </p>
      <button type="button" class="cr-rep-pdf" (click)="downloadReportAsPdf()">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3v10m0 0l-4-4m4 4l4-4M5 19h14"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span>Download as PDF</span>
      </button>
    </div>

    <div class="cr-rep-table-wrap mr-15">
      <table class="cr-rep-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Attendant</th>
            <th>Status</th>
            <th>Shift</th>
            <th>Location</th>
            <th>Opening Balance</th>
            <th>Expected Closing</th>
            <th>Closing Balance</th>
            <th>Delta</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let r of reportData; trackBy: trackByReportRowId">
            <td>{{ r.date }}</td>
            <td>{{ r.attendant }}</td>
            <td>
              <ng-container *ngIf="!statusIsPlain(r); else doubleDash">
                <app-cash-reconciliation-status-badge
                  [status]="r.status_key"
                  [delta]="r.status_key === 'unmatched' ? r.delta : null"
                />
              </ng-container>

              <ng-template #doubleDash>
                <span class="dash">--</span>
              </ng-template>
            </td>
            <td>{{ getShiftLabel(r.shift) || '--' }}</td>
            <td>{{ r.location }}</td>
            <td class="cr-rep-num">
              {{ r.opening_balance === null ? '—' : formatReportCurrency(r.opening_balance) }}
            </td>
            <td class="cr-rep-num">
              {{ r.expected_closing === null ? '—' : formatReportCurrency(r.expected_closing) }}
            </td>
            <td class="cr-rep-num">
              {{ r.closing_balance === null ? '—' : formatReportCurrency(r.closing_balance) }}
            </td>
            <td class="cr-rep-num" [ngClass]="reportDeltaClass(r.delta)">
              {{ r.delta === null ? '—' : formatReportCurrency(r.delta) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="cr-rep-summary-wrap pd-15">
      <div class="cr-rep-summary">
        <div class="cr-rep-sum-card">
          <div class="cr-rep-sum-label">Total Entries</div>
          <div class="cr-rep-sum-value">{{ twoDigit(reportTotalEntries) }}</div>
        </div>
        <div class="cr-rep-sum-card cr-rep-sum-card--ok">
          <div class="cr-rep-sum-label">Total Matched</div>
          <div class="cr-rep-sum-value">{{ twoDigit(reportTotalMatched) }}</div>
        </div>
        <div class="cr-rep-sum-card cr-rep-sum-card--bad">
          <div class="cr-rep-sum-label">Total Not Matched</div>
          <div class="cr-rep-sum-value">{{ twoDigit(reportTotalNotMatched) }}</div>
        </div>
        <div
          class="cr-rep-sum-card"
          [ngClass]="{
            'cr-rep-sum-card--ok': reportSumDeltas === 0,
            'cr-rep-sum-card--bad': reportSumDeltas !== 0,
          }"
        >
          <div class="cr-rep-sum-label">Sum of Deltas</div>
          <div class="cr-rep-sum-value">{{ formatReportCurrency(reportSumDeltas) }}</div>
        </div>
      </div>
    </div>
  </div>
</app-wi-data-modal>

<!-- Generate Reports — step 3: monthly/custom result -->
<app-wi-data-modal
  *ngIf="reportDateRangeModalOpen"
  [isOpen]="reportDateRangeModalOpen"
  title="Cash Reconciliation Report"
  width="900px"
  panelClass="cr-modal cr-modal--report cr-modal--report-result"
  ariaLabel="Cash Reconciliation Report"
  (modalClose)="closeReportDateRangeModal()"
  bodyClass="data-modal-no-bg-body"
>
  <div class="cr-rep-body">
    <div class="data-modal__body--header">
      <p class="cr-rep-meta">
        <span class="cr-rep-dot">•</span>
        <span><strong>Date range:</strong> {{ reportDateRangeLabel }}</span>
        <span class="cr-rep-dot">•</span>
        <span><strong>Location:</strong> {{ reportLocationLabel }}</span>
        <span class="cr-rep-dot">•</span>
        <span><strong>Generated:</strong> {{ formatReportGeneratedAt(reportGeneratedAt) }}</span>
      </p>
      <button type="button" class="cr-rep-pdf" (click)="downloadReportAsPdf()">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3v10m0 0l-4-4m4 4l4-4M5 19h14"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span>Download as PDF</span>
      </button>
    </div>

    <div *ngIf="reportLoading" class="cr-loading">Loading…</div>

    <div *ngIf="!reportLoading && reportData.length === 0">
      <app-empty-result
        image="/assets/images/empty_screen.png"
        title="No records available"
        description="No cash reconciliation data available for this date."
        [showAction]="false"
      >
      </app-empty-result>
    </div>

    <div class="cr-rep-report-grid cr-overflow-auto">
      <app-reconciliation-grid
        *ngIf="!reportLoading && reportData.length > 0"
        class="cr-fix-results"
        [rows]="reportGridData"
        [readonly]="true"
        bodyClass="rg-grid-body pd-15"
      ></app-reconciliation-grid>
      <div class="cr-rep-summary-wrap pd-15">
        <div class="cr-rep-summary">
          <div class="cr-rep-sum-card">
            <div class="cr-rep-sum-label">Total Entries</div>
            <div class="cr-rep-sum-value">{{ twoDigit(reportTotalEntries) }}</div>
          </div>
          <div class="cr-rep-sum-card cr-rep-sum-card--ok">
            <div class="cr-rep-sum-label">Total Matched</div>
            <div class="cr-rep-sum-value">{{ twoDigit(reportTotalMatched) }}</div>
          </div>
          <div class="cr-rep-sum-card cr-rep-sum-card--bad">
            <div class="cr-rep-sum-label">Total Not Matched</div>
            <div class="cr-rep-sum-value">{{ twoDigit(reportTotalNotMatched) }}</div>
          </div>
          <div
            class="cr-rep-sum-card"
            [ngClass]="{
              'cr-rep-sum-card--ok': reportSumDeltas === 0,
              'cr-rep-sum-card--bad': reportSumDeltas !== 0,
            }"
          >
            <div class="cr-rep-sum-label">Sum of Deltas</div>
            <div class="cr-rep-sum-value">{{ formatReportCurrency(reportSumDeltas) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</app-wi-data-modal>

<!-- Mismatch Reason Modal -->
<app-wi-data-modal
  *ngIf="mismatchReasonModalOpen"
  [isOpen]="mismatchReasonModalOpen"
  title="Mismatch Reason"
  width="500px"
  panelClass="cr-modal"
  ariaLabel="Mismatch Reason"
  (modalClose)="closeMismatchReasonModal()"
>
  <div class="pd-15">
    <div class="scr-field">
      <label class="scr-label">
        Closing balance does not match expected closing. Please enter reason.
      </label>

      <textarea
        [(ngModel)]="mismatchReason"
        rows="5"
        class="cr-textarea"
        placeholder="Enter reason"
      ></textarea>
    </div>

    <div class="cr-report-actions">
      <button
        type="button"
        class="cr-report-btn cr-report-btn--secondary"
        (click)="closeMismatchReasonModal()"
      >
        Cancel
      </button>

      <button
        type="button"
        class="cr-report-btn cr-report-btn--primary"
        [disabled]="!mismatchReason.trim() || submittingClosing"
        (click)="submitMismatchReason()"
      >
        {{ submittingClosing ? 'Submitting...' : 'Submit' }}
      </button>
    </div>
  </div>
</app-wi-data-modal>
